import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { ReactComponent as LinkIcon } from "../../../assets/newImages/icons/link-h.svg";
import { ReactComponent as Mail } from "../../../assets/newImages/icons/mail.svg";
import { ReactComponent as Telegram } from "../../../assets/newImages/icons/telegram-alt.svg";
import { ReactComponent as WeChat } from "../../../assets/newImages/icons/wechat.svg";
import { ReactComponent as WhatsApp } from "../../../assets/newImages/icons/whatsapp.svg";
import { Button } from "../../../components/Button";
import { Filtros } from "../../../components/Filtros/Filtros";
import { Input } from "../../../components/Input";
import Loader from "../../../components/Loading/Loader/Loader";
import { Select } from "../../../components/Select";
import { EmpresaContext } from "../../../contexts/Empresa/EmpresaContext";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { LinkAgrupamentoApi } from "../../../hooks/linkAgrupamento";
import { staginEnv } from "../../../services/Functions";
import { CatalogoAgrupamento, LinkAgrupamentoInfo } from "../../../types/LinkAgrupamento";
import { IconsContainer, IconWrapper } from "../styles";
import { LinkAgrupamentoContainer } from "./styles";

const mensagensErro: Record<string, string> = {
  HASH_FORMATO_INVALIDO: "Use de 3 a 30 caracteres: letras minúsculas, números ou hífen.",
  HASH_INDISPONIVEL: "Este nome já está em uso. Escolha outro.",
  MINIMO_CATALOGOS: "Selecione no mínimo 2 catálogos.",
  CATALOGO_INVALIDO: "Algum catálogo selecionado venceu ou foi desativado. Atualize a página e tente novamente.",
  SEM_PERMISSAO: "Você não tem permissão para alterar o link de agrupamento.",
};

type FiltroSelecao = "todos" | "selecionados" | "nao_selecionados";

const opcoesFiltro = [
  { value: "todos", name: "Todos" },
  { value: "selecionados", name: "Selecionados" },
  { value: "nao_selecionados", name: "Não selecionados" },
];

// Mantém o que o usuário digita no formato aceito pela API: minúsculas, números e hífen
const limparHash = (valor: string) =>
  valor
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 30);

const LinkAgrupamento: React.FC = () => {
  const { theme } = useTheme();
  const { empresa } = useContext(EmpresaContext);
  const api = LinkAgrupamentoApi();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<LinkAgrupamentoInfo | null>(null);
  const [catalogos, setCatalogos] = useState<CatalogoAgrupamento[]>([]);
  const [editando, setEditando] = useState(false);

  const [hash, setHash] = useState("");
  const [erroHash, setErroHash] = useState("");
  const [gerandoHash, setGerandoHash] = useState(false);
  const [selecionados, setSelecionados] = useState<number[]>([]);
  const [pesquisa, setPesquisa] = useState("");
  const [filtro, setFiltro] = useState<FiltroSelecao>("todos");
  const [filtroAux, setFiltroAux] = useState<FiltroSelecao>("todos");
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [copiandoLink, setCopiandoLink] = useState(false);

  const minimo = info?.minimo_catalogos ?? 2;

  const abrirFormulario = (dados: LinkAgrupamentoInfo, lista: CatalogoAgrupamento[]) => {
    const disponiveis = new Set(lista.map((catalogo) => catalogo.id));
    setHash(dados.link?.hash ?? "");
    // Catálogo que venceu depois de vinculado não aparece na lista, então sai da seleção
    setSelecionados((dados.link?.catalogos ?? []).filter((id) => disponiveis.has(id)));
    setErroHash("");
    setEditando(true);
  };

  const init = async () => {
    try {
      setLoading(true);
      const dados = await api.buscar();
      setInfo(dados);
      if (dados.pode_editar) {
        const lista = await api.catalogos();
        setCatalogos(lista);
        if (!dados.link) {
          abrirFormulario(dados, lista);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar link de agrupamento:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    init();
  }, []);

  // Último valor enviado para verificação: resposta de um valor antigo que chegar depois é descartada
  const hashEmVerificacao = useRef("");

  // Estável de propósito: o Input recria o debounce quando onDebounce muda
  const verificarHash = useCallback(async (valorDigitado: string) => {
    const valor = limparHash(valorDigitado);
    hashEmVerificacao.current = valor;
    if (!valor) {
      setErroHash("");
      return;
    }
    try {
      const resultado = await LinkAgrupamentoApi().verificarHash(valor);
      if (hashEmVerificacao.current !== valor) return;
      setErroHash(resultado.disponivel ? "" : mensagensErro[resultado.erro ?? ""] ?? "Nome indisponível.");
    } catch (error) {
      if (hashEmVerificacao.current === valor) setErroHash("");
    }
  }, []);

  const gerarHash = async () => {
    try {
      setGerandoHash(true);
      hashEmVerificacao.current = "";
      setHash(await api.gerarHash());
      setErroHash("");
    } finally {
      setGerandoHash(false);
    }
  };

  const alternarCatalogo = (id: number) => {
    setSelecionados((atual) => (atual.includes(id) ? atual.filter((item) => item !== id) : [...atual, id]));
  };

  const salvar = async () => {
    if (erroHash) {
      Swal.fire({ icon: "warning", title: "Confira o nome do link", text: erroHash });
      return;
    }
    if (selecionados.length < minimo) {
      Swal.fire({ icon: "warning", title: "Selecione os catálogos", text: mensagensErro.MINIMO_CATALOGOS });
      return;
    }
    try {
      setSalvando(true);
      const dados = await api.salvar(hash, selecionados);
      setInfo(dados);
      setEditando(false);
    } catch (error: any) {
      const codigo = error?.response?.data?.error;
      if (codigo === "HASH_INDISPONIVEL" || codigo === "HASH_FORMATO_INVALIDO") {
        setErroHash(mensagensErro[codigo]);
      }
      Swal.fire({
        icon: "error",
        title: "Não foi possível salvar o link",
        text: mensagensErro[codigo] ?? "Tente novamente em alguns instantes.",
      });
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async () => {
    const { isConfirmed } = await Swal.fire({
      title: "Tem certeza que deseja excluir o link?",
      text: "O link deixa de funcionar para quem já recebeu e o nome fica livre. Os catálogos não são afetados.",
      icon: "warning",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Excluir",
      showCancelButton: true,
      customClass: {
        confirmButton: "btn-confirm",
        cancelButton: "btn-cancel",
        icon: "custom-swal-icon",
      },
    });
    if (!isConfirmed) return;

    try {
      setExcluindo(true);
      const dados = await api.excluir();
      setInfo(dados);
      // Volta ao formulário vazio, como na primeira vez
      setPesquisa("");
      setFiltro("todos");
      setFiltroAux("todos");
      abrirFormulario(dados, catalogos);
    } catch (error: any) {
      const codigo = error?.response?.data?.error;
      Swal.fire({
        icon: "error",
        title: "Não foi possível excluir o link",
        text: mensagensErro[codigo] ?? "Tente novamente em alguns instantes.",
      });
    } finally {
      setExcluindo(false);
    }
  };

  const catalogosFiltrados = useMemo(() => {
    const termo = pesquisa.trim().toLowerCase();
    return catalogos.filter((catalogo) => {
      if (termo && !catalogo.descricao.toLowerCase().includes(termo)) return false;
      const selecionado = selecionados.includes(catalogo.id);
      if (filtro === "selecionados") return selecionado;
      if (filtro === "nao_selecionados") return !selecionado;
      return true;
    });
  }, [catalogos, pesquisa, filtro, selecionados]);

  // Mesmo padrão do dominio_https (empresaApi.ts): sem domínio, local abre o catálogo em localhost:5173;
  // em dev acrescenta ?env=staging (staginEnv), como nos links de catálogo
  const baseLink =
    info?.dominio === "app.conectavenda.com.br" && window.location.hostname === "localhost"
      ? "localhost:5173"
      : info?.dominio ?? "";
  const protocolo = baseLink.startsWith("localhost") ? "http" : "https";
  const url = info?.link ? `${protocolo}://${baseLink}/${info.link.hash}${staginEnv()}` : "";
  const mensagem = encodeURIComponent(empresa?.mensagem_whats || "Olá, veja os catálogos da minha empresa:");

  const icons = [
    {
      component: LinkIcon,
      label: copiandoLink ? "Link copiado" : "Link",
      funcao: () => {
        navigator.clipboard.writeText(url).then(
          () => {
            setCopiandoLink(true);
            setTimeout(() => setCopiandoLink(false), 2000);
          },
          () => {
            Swal.fire({
              icon: "error",
              title: "Erro ao copiar link",
              text: `Mas você ainda pode tentar copiar o link manualmente! \n ${url}`,
            });
          },
        );
      },
    },
    {
      component: WhatsApp,
      label: "WhatsApp",
      funcao: () => window.open(`https://api.whatsapp.com/send?text=${mensagem}${url}`),
    },
    {
      component: Telegram,
      label: "Telegram",
      funcao: () => window.open(`https://telegram.me/share/url?url=${url}&text=${mensagem}`),
    },
    {
      component: Mail,
      label: "E-mail",
      funcao: () => window.open(`mailto:?subject=${mensagem}&body=${url}`),
    },
    {
      component: WeChat,
      label: "WeChat",
      funcao: () => window.open(`https://www.addtoany.com/add_to/wechat?linkurl=${url}`),
    },
  ];

  if (loading) {
    return (
      <LinkAgrupamentoContainer theme={theme}>
        <Loader />
      </LinkAgrupamentoContainer>
    );
  }

  if (!info) {
    return (
      <LinkAgrupamentoContainer theme={theme}>
        <div className="cabecalho">
          <div className="titulo fonteTitulo">Link de agrupamento</div>
        </div>
        <div className="aviso fonteTexto">Não foi possível carregar o link de agrupamento. Atualize a página.</div>
      </LinkAgrupamentoContainer>
    );
  }

  const rotuloDominio = info.dominio === "app.conectavenda.com.br"
    ? "Link"
    : info.dominio.includes("conectavenda")
      ? "Subdomínio"
      : "Domínio";

  return (
    <LinkAgrupamentoContainer theme={theme}>
      <div className="cabecalho">
        <div className="titulo fonteTitulo">Link de agrupamento</div>
        <div className="descricao fonteTexto">
          Crie um link de agrupamento com os catálogos selecionados e compartilhe com seus clientes, tudo em um só lugar.
        </div>
      </div>

      {!editando && !info.link && (
        <div className="aviso fonteTexto">
          Nenhum link de agrupamento criado ainda. Peça para um usuário com acesso às configurações criar o link.
        </div>
      )}

      {!editando && info.link && (
        <>
          <div className="bloco">
            <div className="linha-titulo">
              <div className="subtitulo fonteSubtitulo">Link gerado</div>
              {info.pode_editar && (
                <div className="editar fonteTexto" onClick={() => abrirFormulario(info, catalogos)}>
                  Editar informações
                </div>
              )}
            </div>
            <a className="link-gerado fonteTexto" href={url} target="_blank" rel="noopener noreferrer">
              {url}
            </a>
          </div>

          <div className="bloco">
            <div className="subtitulo fonteSubtitulo">Compartilhe via</div>
            <IconsContainer theme={theme} className="canais">
              {icons.map((icon, index) => (
                <IconWrapper key={index} firstTwo={index < 2} theme={theme} onClick={icon.funcao}>
                  <div className="bola">
                    <icon.component />
                  </div>
                  <div className="texto fonteRotulos">{icon.label}</div>
                </IconWrapper>
              ))}
            </IconsContainer>
          </div>
        </>
      )}

      {editando && (
        <>
          <div className="bloco">
            <div className="rotulo fonteTexto">{rotuloDominio}</div>
            <div className="linha-hash">
              <span className="prefixo fonteTexto">{baseLink}/</span>
              <div className="campo-hash">
                <Input
                  type="text"
                  placeholder="nomedasuamarca"
                  value={hash}
                  maxLength={30}
                  onChange={(valor) => {
                    setHash(limparHash(valor));
                    setErroHash("");
                    hashEmVerificacao.current = "";
                  }}
                  onDebounce={verificarHash}
                  debounceTime={500}
                />
              </div>
              <div className={`botao fonteTexto ${gerandoHash ? "desabilitado" : ""}`} onClick={() => !gerandoHash && gerarHash()}>
                Gerar
              </div>
            </div>
            {erroHash ? (
              <div className="erro fonteRotulos">{erroHash}</div>
            ) : (
              <div className="dica fonteRotulos">
                Escolha um nome ou clique em Gerar. Se deixar em branco, usamos o nome da sua empresa.
              </div>
            )}
          </div>

          <div className="bloco">
            <div className="rotulo fonteTexto">
              Selecione os catálogos
              <span className="contador">
                {" "}({selecionados.length} {selecionados.length === 1 ? "selecionado" : "selecionados"}, mínimo {minimo})
              </span>
            </div>
            <div className="pesquisa-filtro">
              <div className="pesquisa">
                <Input
                  type="text"
                  icone="icons/search.svg"
                  placeholder="Pesquisar por título"
                  value={pesquisa}
                  onChange={(valor) => setPesquisa(valor)}
                />
              </div>
              <div className="filtro">
                <Filtros
                  onAplicar={() => setFiltro(filtroAux)}
                  onCancelar={() => setFiltroAux(filtro)}
                  onReset={() => {
                    setFiltroAux("todos");
                    setFiltro("todos");
                  }}
                  activeFiltersCount={filtro === "todos" ? 0 : 1}
                >
                  <Select
                    options={opcoesFiltro}
                    value={filtroAux}
                    label="Exibir"
                    onChange={(valor) => setFiltroAux(valor as FiltroSelecao)}
                  />
                </Filtros>
              </div>
            </div>

            {catalogosFiltrados.length === 0 ? (
              <div className="aviso fonteTexto">Nenhum catálogo encontrado.</div>
            ) : (
              <div className="grid-catalogos">
                {catalogosFiltrados.map((catalogo) => {
                  const selecionado = selecionados.includes(catalogo.id);
                  return (
                    <div key={catalogo.id} className={`card-catalogo ${selecionado ? "selecionado" : ""}`}>
                      {catalogo.banner ? (
                        <img className="banner" src={catalogo.banner} alt={catalogo.descricao} />
                      ) : (
                        <div className="banner sem-banner" />
                      )}
                      <div className="informacoes">
                        <div className="titulo-card">
                          <div className="nome fonteTexto">{catalogo.descricao}</div>
                          <div className="quantidade fonteTexto">
                            {catalogo.qtd_produtos} {catalogo.qtd_produtos === 1 ? "produto" : "produtos"}
                          </div>
                        </div>
                        {selecionado ? (
                          <div className="botao remover fonteTexto" onClick={() => alternarCatalogo(catalogo.id)}>
                            Remover
                          </div>
                        ) : (
                          <Button title="Adicionar" padding="2rem" onClick={() => alternarCatalogo(catalogo.id)} />
                        )}
                        {catalogo.usuario_email && (
                          <div className="gerenciado fonteRotulos">
                            <div>Gerenciado por:</div>
                            <div className="usuario" title={catalogo.usuario_email}>
                              {catalogo.usuario_email}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="acoes">
            <div
              className={`botao fonteTexto ${info.link ? "" : "desabilitado"}`}
              title={info.link ? "" : "Salve o link para visualizar"}
              onClick={() => info.link && window.open(url, "_blank")}
            >
              Visualizar
            </div>
            <Button
              title="Salvar"
              width="100%"
              padding="0"
              loading={salvando}
              onClick={salvar}
            />
          </div>
          {info.link && (
            <div className="acoes-edicao">
              <div className="cancelar fonteRotulos" onClick={() => setEditando(false)}>
                Cancelar edição
              </div>
              <div
                className={`excluir fonteRotulos ${excluindo ? "desabilitado" : ""}`}
                onClick={() => !excluindo && excluir()}
              >
                Excluir link
              </div>
            </div>
          )}
        </>
      )}
    </LinkAgrupamentoContainer>
  );
};

export default LinkAgrupamento;
